import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { isBefore, isToday } from 'date-fns';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL, LabelS } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge';
import { Empty } from '@sorare/core/src/components/cards/Empty';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import InlineGame from '@sorare/gameplay/src/components/stats/InlineGame';
import { useNBAEvents } from '@sorare/gameplay/src/lib/events';

import {
  NBAOnboardingSelectStarPlayerQuery,
  NBAOnboardingSelectStarPlayerQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  @media ${laptopAndAbove} {
    overflow-y: auto;
    height: min(calc(100vh - var(--unit) * 8), 800px);
  }
`;

const TextInfo = styled(Vertical)`
  gap: var(--double-unit);
  padding: var(--double-unit);
`;

const CardsWrapper = styled.div`
  display: grid;
  gap: var(--double-unit);
  padding-inline: var(--double-unit);
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));

  @media ${tabletAndAbove} {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  @media ${laptopAndAbove} {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
`;

const CardWrapper = styled.div`
  width: 100%;
  position: relative;
  isolation: isolate;
  aspect-ratio: var(--card-aspect-ratio);
`;

const Card = styled(ResponsiveImg)`
  width: 100%;
  cursor: pointer;
  aspect-ratio: var(--card-aspect-ratio);
  user-select: none;

  &.selected {
    border-radius: var(--intermediate-unit);
    outline: 4px solid var(--c-brand-600);
  }
`;

const ButtonWrapper = styled.div`
  bottom: 0;
  position: sticky;
  margin-top: auto;
  padding: var(--double-unit);
`;

const StyledButton = styled(Button)`
  box-shadow: 0px 0px var(--double-unit) var(--c-neutral-100);
`;

const CornerBadgeWrapper = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 50px;
  right: 0;
`;

const BadgeContent = styled(LabelS)`
  text-wrap: nowrap;
`;

const InlineGameWrapper = styled.div`
  border-radius: var(--unit);
  padding: var(--half-unit) var(--unit);
  background: rgba(var(--c-rgb-neutral-1000), 0.1);
`;

const EmptySearchState = styled.div`
  margin: auto;
`;

export type NBACommonStarPlayer =
  NBAOnboardingSelectStarPlayerQuery['commonPlayers']['nodes'][0];

type Props = {
  setStep: (step: number) => void;
  setSelectedPlayer: Dispatch<SetStateAction<NBACommonStarPlayer | undefined>>;
};

const query = gql`
  query NBAOnboardingSelectStarPlayerQuery {
    commonPlayers(sport: NBA, quality: TIER_0) {
      nodes {
        id
        anyPlayer {
          slug
          displayName
          nextGame(so5FixtureEligible: true) {
            id
            date
            ...InlineGame_game
          }
        }
        pictureUrl
      }
    }
    currentUser {
      slug
      nbaUserProfile {
        id
        onboardingSo5Fixture {
          slug
          endDate
        }
      }
    }
  }
  ${InlineGame.fragments.game}
` as TypedDocumentNode<
  NBAOnboardingSelectStarPlayerQuery,
  NBAOnboardingSelectStarPlayerQueryVariables
>;

export const SelectStarPlayer = ({ setStep, setSelectedPlayer }: Props) => {
  const track = useNBAEvents();
  const { data } = useQuery(query);
  const [selected, setSelected] = useState<NBACommonStarPlayer>();

  const players = data?.commonPlayers.nodes;
  const onboardingSo5Fixture =
    data?.currentUser?.nbaUserProfile?.onboardingSo5Fixture;

  useEffect(() => {
    track('View Onboarding Select star player');
  }, [track]);

  return (
    <Wrapper>
      <TextInfo>
        <HeadlineL brand className="text-center">
          <FormattedMessage
            id="S3Onboarding.SelectStarPlayer.title"
            defaultMessage="Select a Star Player!"
          />
        </HeadlineL>
      </TextInfo>
      <CardsWrapper>
        {players?.map(player => {
          const nextGameDate = player.anyPlayer.nextGame?.date;
          const isPlayingSoon =
            onboardingSo5Fixture?.endDate &&
            nextGameDate &&
            isBefore(nextGameDate, onboardingSo5Fixture.endDate);

          return (
            <Vertical key={player.id} gap={1}>
              <CardWrapper>
                <Card
                  cropWidth={320}
                  src={player.pictureUrl}
                  alt={player.anyPlayer.displayName}
                  onClick={() => setSelected(player)}
                  className={classNames({
                    selected: selected?.id === player.id,
                  })}
                />
                {isPlayingSoon && (
                  <CornerBadgeWrapper>
                    <CornerBadge color="var(--c-rivals-win)">
                      <BadgeContent bold>
                        <FormattedMessage
                          id="S3Onboarding.SelectStarPlayer.playsSoon"
                          defaultMessage="Plays soon"
                        />
                      </BadgeContent>
                    </CornerBadge>
                  </CornerBadgeWrapper>
                )}
              </CardWrapper>
              {nextGameDate && (
                <Vertical center gap={0.5}>
                  <InlineGameWrapper>
                    <InlineGame
                      inline
                      size="sm"
                      withTeamCode
                      disableTooltip
                      game={player.anyPlayer.nextGame}
                    />
                  </InlineGameWrapper>
                  <LabelS bold color="var(--c-neutral-700)">
                    {isToday(nextGameDate) ? (
                      <>
                        <FormattedMessage {...glossary.today} />
                        ,&nbsp;
                        <FormattedDate
                          value={nextGameDate}
                          hour="numeric"
                          minute="numeric"
                        />
                      </>
                    ) : (
                      <FormattedDate
                        value={nextGameDate}
                        day="numeric"
                        month="short"
                        hour="numeric"
                        minute="numeric"
                      />
                    )}
                  </LabelS>
                </Vertical>
              )}
            </Vertical>
          );
        })}
      </CardsWrapper>
      {players?.length === 0 && (
        <EmptySearchState>
          <Empty
            title={
              <FormattedMessage
                id="S3Onboarding.SelectStarPlayer.emptySearch.title"
                defaultMessage="Player not found"
              />
            }
            description={
              <FormattedMessage
                id="S3Onboarding.SelectStarPlayer.emptySearch.description"
                defaultMessage="Try searching for another player"
              />
            }
          />
        </EmptySearchState>
      )}
      <ButtonWrapper>
        <StyledButton
          fullWidth
          disabled={!selected}
          size="large"
          color="secondary"
          onClick={() => {
            if (selected) {
              setSelectedPlayer(selected);
              track('Click Onboarding Select star player', {
                player: selected.anyPlayer.slug,
              });
              setStep(4);
            }
          }}
        >
          {!selected ? (
            <FormattedMessage
              id="S3Onboarding.SelectStarPlayer.cta"
              defaultMessage="Select a Star Player"
            />
          ) : (
            <FormattedMessage {...glossary.confirm} />
          )}
        </StyledButton>
      </ButtonWrapper>
    </Wrapper>
  );
};
